(() => {
  const eventSearchCache = new Map();
  const contentResultsDefaults = new WeakMap();
  const treatmentResultsDefaults = new WeakMap();
  const productTypeAliases = {
    workshop: ['workshop', 'סדנה', 'סדנאות'],
    retreat: ['retreat', 'ריטריט', 'ריטריטים'],
    course: ['course', 'קורס', 'קורסים'],
    festival: ['festival', 'פסטיבל', 'פסטיבלים'],
    show: ['show', 'party', 'performance', 'הופעה', 'הופעות'],
    online: ['online', 'אונליין'],
  };

  const eventSubfiltersByProductType = {
    all: ['event-type', 'region', 'date-range'],
    workshop: ['event-type', 'region', 'audience', 'price', 'date-range', 'category'],
    retreat: ['event-type', 'region', 'location', 'audience', 'duration', 'retreat-price', 'date-range', 'category'],
    course: ['event-type', 'region', 'format', 'date-range', 'category'],
    festival: ['event-type', 'region', 'date-range'],
    show: ['event-type', 'region', 'date-range'],
    online: ['event-type', 'price', 'date-range', 'category'],
  };

  const normalizeEventSearchTerm = (value) => value.trim().replace(/^product_type:event\s*/i, '');

  const normalizeProductType = (value) => {
    const normalizedValue = String(value || '').trim().toLocaleLowerCase();
    if (!normalizedValue || normalizedValue === 'all') return 'all';

    return (
      Object.entries(productTypeAliases).find(([, aliases]) => aliases.includes(normalizedValue))?.[0] || ''
    );
  };

  const collectionItemMatchesProductType = (item, productType) => {
    if (productType === 'all') return true;

    return normalizeProductType(item.dataset.spiritEventProductType) === productType;
  };

  const normalizeEventMetaValue = (value) => String(value || '').trim();

  const getEventStartTimestamp = (item) => {
    const timestamp = Date.parse(item?.dataset.spiritEventStart || '');
    return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
  };

  const getEventMonthValue = (item) => {
    const explicitMonth = normalizeEventMetaValue(item.dataset.spiritEventMonth);
    if (explicitMonth) return explicitMonth;

    const timestamp = getEventStartTimestamp(item);
    if (!Number.isFinite(timestamp)) return '';

    const date = new Date(timestamp);
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
  };

  const getEventMonthLabel = (value) => {
    const normalizedValue = normalizeEventMetaValue(value);
    const match = normalizedValue.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?$/);
    if (!match) return normalizedValue;

    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
    return new Intl.DateTimeFormat('he-IL', { month: 'long', year: 'numeric' }).format(date);
  };

  const getEventRegionValue = (item) => normalizeEventMetaValue(item?.dataset.spiritEventRegion);

  const getEventRegionLabel = (item) =>
    normalizeEventMetaValue(item?.dataset.spiritEventRegionLabel) || getEventRegionValue(item);

  const normalizeEventLocationScope = (value) => normalizeEventMetaValue(value).toLocaleLowerCase();

  const getEventLocationScopeValue = (item) => {
    const explicitScope = normalizeEventLocationScope(item?.dataset.spiritEventLocationScope);
    if (explicitScope) return explicitScope;

    return getEventRegionValue(item) ? 'israel' : '';
  };

  const getEventLocationScopeLabel = (value) => {
    if (value === 'israel') return 'ישראל';
    if (value === 'abroad' || value === 'overseas' || value === 'outside_israel') return 'חו״ל';
    return value;
  };

  const getEventAudienceValue = (item) => normalizeEventMetaValue(item?.dataset.spiritEventAudience);

  const getEventAudienceLabel = (item) =>
    normalizeEventMetaValue(item?.dataset.spiritEventAudienceLabel) || getEventAudienceValue(item);

  const getEventFormatValue = (item) => normalizeEventMetaValue(item?.dataset.spiritEventFormat);

  const getEventFormatLabel = (value) => {
    const labels = {
      physical: 'פיזי',
      online: 'אונליין',
      hybrid: 'היברידי',
    };
    return labels[String(value || '').toLocaleLowerCase()] || value;
  };

  const getEventDurationValue = (item) => {
    const startTimestamp = getEventStartTimestamp(item);
    const endTimestamp = Date.parse(item?.dataset.spiritEventEnd || '');
    if (!Number.isFinite(startTimestamp)) return '';

    const durationInDays = Math.max(
      1,
      Math.round(
        ((Number.isFinite(endTimestamp) ? endTimestamp : startTimestamp) - startTimestamp) / 86400000
      ) + 1
    );

    if (durationInDays === 1) return '1';
    if (durationInDays === 2) return '2';
    if (durationInDays <= 5) return '3-5';
    if (durationInDays <= 8) return '6-8';
    return '9+';
  };

  const getEventDurationLabel = (value) => {
    const labels = {
      1: 'יום אחד',
      2: 'יומיים',
      '3-5': '3–5 ימים',
      '6-8': '6–8 ימים',
      '9+': '9 ימים ומעלה',
    };
    return labels[value] || value;
  };

  const getEventSearchQuery = (term) => (term ? `product_type:event ${term}` : 'product_type:event');

  const createElementFromMarkup = (markup) => {
    const template = document.createElement('template');
    template.innerHTML = markup.trim();
    return template.content.firstElementChild;
  };

  const initializeContentOverride = () => {
    const target = document.querySelector('[data-spirit-content-results-target]');
    const rail = target?.querySelector('[data-spirit-results-rail]');
    if (!target || !rail) return;

    if (!contentResultsDefaults.has(target)) {
      contentResultsDefaults.set(target, {
        railMarkup: rail.innerHTML,
        targetHidden: target.hidden,
        viewAllLinks: Array.from(target.querySelectorAll('.spirit-results__view-all')).map((link) => ({
          element: link,
          href: link.getAttribute('href'),
          hidden: link.hidden,
        })),
      });
    }

    const defaultState = contentResultsDefaults.get(target);
    const override = document.querySelector('[data-spirit-content-override]');
    const overrideItems = override?.content?.querySelectorAll('.spirit-results__item');

    if (!override || !overrideItems?.length) {
      if (target.dataset.spiritContentOverrideApplied !== 'true') return;

      rail.innerHTML = defaultState.railMarkup;
      target.hidden = defaultState.targetHidden;
      defaultState.viewAllLinks.forEach(({ element, href, hidden }) => {
        if (href === null) {
          element.removeAttribute('href');
        } else {
          element.setAttribute('href', href);
        }
        element.hidden = hidden;
      });
      delete target.dataset.spiritContentOverrideApplied;
      return;
    }

    rail.replaceChildren(override.content.cloneNode(true));
    target.hidden = false;

    const viewAllUrl = override.dataset.spiritContentViewAllUrl;
    if (viewAllUrl) {
      defaultState.viewAllLinks.forEach(({ element }) => {
        element.href = viewAllUrl;
        element.hidden = false;
      });
    }

    target.dataset.spiritContentOverrideApplied = 'true';
  };

  // Mixed Shopify search paginates products and articles together. Fetch the
  // article section separately so a product-heavy first page cannot starve it.
  const initializeArticleResults = () => {
    const target = document.querySelector('[data-spirit-article-search-url]');
    if (!target || target.dataset.spiritArticleSearchReady === 'true') return;
    if (new URLSearchParams(window.location.search).get('type') === 'article') return;
    target.dataset.spiritArticleSearchReady = 'true';
    const url = target.dataset.spiritArticleSearchUrl;
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Article search failed: ${response.status}`);
        return response.text();
      })
      .then((markup) => {
        if (!target.isConnected || target.dataset.spiritArticleSearchUrl !== url) return;
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const source = parsed.querySelector('[data-spirit-content-results-target] [data-spirit-results-rail]');
        if (!source) return;
        const items = Array.from(source.children).slice(0, Number(target.dataset.spiritContentLimit) || 4);
        const holder = document.createElement('div');
        holder.append(...items);
        const defaults = contentResultsDefaults.get(target);
        if (defaults) {
          defaults.railMarkup = holder.innerHTML;
          defaults.targetHidden = items.length === 0;
        }
        // A configured emotion blog takes precedence over keyword search.
        if (target.dataset.spiritContentOverrideApplied === 'true') return;
        target.querySelector('[data-spirit-results-rail]').replaceChildren(...holder.children);
        target.hidden = items.length === 0;
      })
      .catch(() => {
        // Preserve the server-rendered content and its full-search link on failure.
        delete target.dataset.spiritArticleSearchReady;
      });
  };

  const initializeTreatmentOverride = () => {
    // The catalog resolves the same category override against every page.
    // Keep this Liquid fallback for stores where the catalog asset is absent.
    if (window.SpiritCatalog) return;
    const target = document.querySelector('[data-spirit-treatment-results-target]');
    const rail = target?.querySelector('[data-spirit-results-rail]');
    if (!target || !rail) return;

    if (!treatmentResultsDefaults.has(target)) {
      treatmentResultsDefaults.set(target, {
        railMarkup: rail.innerHTML,
        targetHidden: target.hidden,
      });
    }

    const defaultState = treatmentResultsDefaults.get(target);
    const override = document.querySelector('[data-spirit-treatment-override]');
    const overrideItems = override?.content?.querySelectorAll('.spirit-results__item');

    if (!override || !overrideItems?.length) {
      if (target.dataset.spiritTreatmentOverrideApplied !== 'true') return;

      rail.innerHTML = defaultState.railMarkup;
      target.hidden = defaultState.targetHidden;
      delete target.dataset.spiritTreatmentOverrideApplied;
      return;
    }

    rail.replaceChildren(override.content.cloneNode(true));
    target.hidden = false;
    target.dataset.spiritTreatmentOverrideApplied = 'true';
  };

  const closeMobileFilterShells = (root = document) => {
    root.querySelectorAll?.('[data-spirit-mobile-filter-shell]').forEach((shell) => {
      shell.classList.remove('is-mobile-filters-open', 'is-mobile-categories-open');
      shell
        .querySelectorAll('[data-spirit-mobile-filter-toggle], [data-spirit-mobile-category-toggle], [data-spirit-mobile-category-proxy]')
        .forEach((button) => button.setAttribute('aria-expanded', 'false'));
      shell.querySelector('[data-spirit-category-disclosure]')?.classList.remove('is-open');
      shell.querySelector('[data-spirit-category-toggle]')?.setAttribute('aria-expanded', 'false');
      shell.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
    });

    root.querySelectorAll?.('[data-spirit-mobile-collection-heading]').forEach((heading) => {
      heading.hidden = false;
    });
  };

  const initializeResults = (root = document) => {
    window.SpiritCatalog?.initialize(root);
    initializeContentOverride();
    initializeArticleResults();
    initializeTreatmentOverride();

    root.querySelectorAll('[data-spirit-search-filter-toggle]').forEach((toggle) => {
      if (toggle.dataset.spiritReady) return;
      const toolbar = toggle.closest('.spirit-results-toolbar--search');
      const setOpen = (open) => {
        toolbar.classList.toggle('is-search-filters-open', open);
        toggle.setAttribute('aria-expanded', String(open));
      };
      toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
      toolbar.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        setOpen(false);
        toggle.focus();
      });
      toggle.dataset.spiritReady = 'true';
    });

    const resultsSections = root.matches?.('[data-spirit-results]')
      ? [root]
      : Array.from(root.querySelectorAll('[data-spirit-results]'));

    resultsSections.forEach((section) => {
      if (section.dataset.spiritFilterAutoCloseReady === 'true') return;

      const closeMobileFilterShell = () => closeMobileFilterShells(section);

      closeMobileFilterShell();

      section.querySelectorAll('.spirit-results-toolbar--search [data-spirit-single-select-filter-param]').forEach((link) => {
        const url = new URL(window.location.href);
        const paramName = link.dataset.spiritSingleSelectFilterParam;
        const paramValue = link.dataset.spiritSingleSelectFilterValue;
        url.searchParams.delete(paramName);
        url.searchParams.delete('page');
        if (!link.classList.contains('is-active') && paramValue) url.searchParams.set(paramName, paramValue);
        if (paramName === 'filter.p.product_type') url.searchParams.delete('event_type');
        link.href = url.toString();
      });

      const filterDropdownSelector = [
        '.spirit-results-toolbar__collection-filters details',
        '.spirit-results-toolbar__mobile-filter',
        '.spirit-results-toolbar__meta-sort',
      ].join(', ');

      const closeOtherFilterDropdowns = (dropdown) => {
        section.querySelectorAll(filterDropdownSelector).forEach((otherDropdown) => {
          if (
            !otherDropdown.open ||
            otherDropdown === dropdown ||
            otherDropdown.contains(dropdown) ||
            dropdown.contains(otherDropdown)
          ) {
            return;
          }

          otherDropdown.removeAttribute('open');
        });
      };

      section.querySelectorAll(filterDropdownSelector).forEach((dropdown) => {
        if (dropdown.dataset.spiritFilterAccordionReady === 'true') return;

        dropdown.addEventListener('toggle', () => {
          if (!dropdown.open) return;
          closeOtherFilterDropdowns(dropdown);
        });

        dropdown.dataset.spiritFilterAccordionReady = 'true';
      });

      section.addEventListener(
        'click',
        (event) => {
          const summary = event.target.closest('summary');
          const dropdown = summary?.parentElement;
          if (dropdown?.matches(filterDropdownSelector)) {
            closeOtherFilterDropdowns(dropdown);
          }

          const selectedFilter = event.target.closest(
            '.spirit-results-toolbar__choice, .spirit-results-toolbar__mobile-value, .spirit-results-toolbar__submit, [data-spirit-event-type-button], [data-spirit-event-meta-sort-option], [data-spirit-event-meta-filter-value], [data-spirit-category-disclosure] a'
          );
          if (!selectedFilter) return;

          selectedFilter.closest('details[open]')?.removeAttribute('open');
          closeMobileFilterShell();

          const categoryDisclosure = selectedFilter.closest('[data-spirit-category-disclosure]');
          if (categoryDisclosure?.classList.contains('is-open')) {
            categoryDisclosure.classList.remove('is-open');
            categoryDisclosure
              .querySelector('[data-spirit-category-toggle]')
              ?.setAttribute('aria-expanded', 'false');
          }
        },
        true
      );

      section.addEventListener('change', (event) => {
        if (!event.target.closest('[data-spirit-mobile-filter-shell]')) return;
        if (event.target.matches('input[type="search"]')) return;

        closeMobileFilterShell();
      });

      section.dataset.spiritFilterAutoCloseReady = 'true';
    });

    root.querySelectorAll('[data-spirit-load-more]').forEach((button) => {
      if (button.dataset.spiritLoadMoreReady === 'true') return;

      button.addEventListener('click', async (event) => {
        event.preventDefault();
        if (button.getAttribute('aria-disabled') === 'true') return;

        const currentGrid = button.closest('.spirit-results__inner--products');
        const currentList = currentGrid?.querySelector('.spirit-results__product-grid');
        const requestUrl = button.href;
        if (!currentGrid?.id || !currentList || !requestUrl) {
          window.location.assign(requestUrl);
          return;
        }

        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        button.classList.add('is-loading');
        currentList.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(requestUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
          });
          if (!response.ok) throw new Error(`Load more failed with status ${response.status}`);

          const html = await response.text();
          const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
          const nextGrid = parsedDocument.getElementById(currentGrid.id);
          const nextList = nextGrid?.querySelector('.spirit-results__product-grid');
          if (!nextList) throw new Error('The next product grid was not found');

          currentList.append(...Array.from(nextList.children));

          const nextButton = nextGrid.querySelector('[data-spirit-load-more]');
          if (nextButton) {
            const nextHref = nextButton.getAttribute('href');
            button.href = new URL(nextHref, response.url || requestUrl).toString();
            button.removeAttribute('aria-disabled');
            button.removeAttribute('aria-busy');
            button.classList.remove('is-loading');
          } else {
            button.closest('.spirit-results__load-more-wrap')?.remove();
          }

          initializeResults(currentGrid);
        } catch (error) {
          window.location.assign(requestUrl);
        } finally {
          currentList.removeAttribute('aria-busy');
        }
      });

      button.dataset.spiritLoadMoreReady = 'true';
    });

    root.querySelectorAll('[data-spirit-event-search]').forEach((form) => {
      if (form.dataset.spiritEventSearchReady === 'true') return;

      const input = form.querySelector('input[name="q"]');
      const targetId = form.dataset.spiritLiveSearchTarget;
      const sectionRoot = form.closest('[data-spirit-results]');
      const isSearchPage = form.dataset.spiritSearchScope === 'search';
      const emotionCategories = () => {
        const params = new URLSearchParams(window.location.search);
        return params.has('emotion') ? (params.get('spirit_categories') || '').split(',').filter(Boolean) : [];
      };
      const collectionSearchSource = isSearchPage
        ? document.createElement('template')
        : sectionRoot?.querySelector('[data-spirit-collection-search-source]');
      const eventTypeControls = sectionRoot?.querySelector('[data-spirit-event-types]');
      const eventTypeButtons = Array.from(
        eventTypeControls?.querySelectorAll('[data-spirit-event-type-button]') || []
      );
      const eventAbroadButtons = Array.from(
        sectionRoot?.querySelectorAll('[data-spirit-event-abroad-button]') || []
      );
      const eventAbroadLinks = Array.from(
        sectionRoot?.querySelectorAll('[data-spirit-event-abroad-link]') || []
      );
      const sortForm = sectionRoot?.querySelector('.spirit-results-toolbar__sort-form');
      const eventMetaFilters = sectionRoot?.querySelector('[data-spirit-event-meta-filters]');
      const eventMetaFilterControls = Array.from(
        eventMetaFilters?.querySelectorAll('[data-spirit-event-meta-filter]') || []
      );
      const eventSubfilterControls = Array.from(
        sectionRoot?.querySelectorAll('[data-spirit-event-subfilter]') || []
      );
      const nativeEventTypeLinks = Array.from(
        sectionRoot?.querySelectorAll('a[data-spirit-event-type-value]') || []
      );
      const singleSelectFilterLinks = Array.from(
        sectionRoot?.querySelectorAll('a[data-spirit-single-select-filter-param]') || []
      );
      const eventMetaSort = sectionRoot?.querySelector('[data-spirit-event-meta-sort]');
      const eventMetaSortButtons = Array.from(
        eventMetaSort?.querySelectorAll('[data-spirit-event-meta-sort-option]') || []
      );
      const status = form.querySelector('[data-spirit-live-search-status]');
      const initialGrid = targetId ? document.getElementById(targetId)?.cloneNode(true) : null;
      const count = sectionRoot?.querySelector('.spirit-results-hero__count');
      const initialCountMarkup = count?.innerHTML || '';
      const initialCountText = count?.textContent.trim() || '';
      const delay = Number.parseInt(form.dataset.spiritLiveSearchDelay, 10) || 350;
      let debounceTimer;
      let abortController;
      let lastAppliedTerm = '';
      const toolbarSelectedEventType = sectionRoot
        ?.querySelector('[data-spirit-selected-event-type]')
        ?.dataset.spiritSelectedEventType;
      let selectedEventType =
        normalizeProductType(
          new URLSearchParams(window.location.search).get('event_type') || toolbarSelectedEventType
        ) || 'all';
      let selectedEventMonth = normalizeEventMetaValue(
        new URLSearchParams(window.location.search).get('event_month')
      );
      let selectedEventRegion = normalizeEventMetaValue(
        new URLSearchParams(window.location.search).get('event_region')
      );
      let selectedEventLocationScope = normalizeEventLocationScope(
        new URLSearchParams(window.location.search).get('event_location_scope')
      );
      let selectedEventAudience = normalizeEventMetaValue(
        new URLSearchParams(window.location.search).get('event_audience')
      );
      let selectedEventFormat = normalizeEventMetaValue(
        new URLSearchParams(window.location.search).get('event_format')
      );
      let selectedEventDuration = normalizeEventMetaValue(
        new URLSearchParams(window.location.search).get('event_duration')
      );
      const defaultEventSortApplied = isSearchPage || Boolean(eventMetaSort);
      let selectedEventSort = new URLSearchParams(window.location.search).get('event_sort') || 'nearest';
      let eventMetaSortApplied = defaultEventSortApplied || new URLSearchParams(window.location.search).has('event_sort');

      if (!['nearest', 'farthest'].includes(selectedEventSort)) {
        selectedEventSort = 'nearest';
        eventMetaSortApplied = defaultEventSortApplied;
      }

      const setStatus = (message) => {
        if (status) status.textContent = message;
      };

      const setLoading = (loading) => {
        form.classList.toggle('is-loading', loading);
        input?.setAttribute('aria-busy', loading ? 'true' : 'false');
        const currentGrid = targetId ? document.getElementById(targetId) : null;
        if (currentGrid) currentGrid.setAttribute('aria-busy', loading ? 'true' : 'false');
      };

      const getCollectionSourceItems = () =>
        Array.from(
          collectionSearchSource?.content?.querySelectorAll('[data-spirit-collection-search-item]') || []
        );

      let searchSourcePromise;
      const loadResultSource = () => {
        if (!collectionSearchSource) return Promise.resolve();
        if (searchSourcePromise) return searchSourcePromise;

        searchSourcePromise = (async () => {
          const items = new Map();
          const collectItems = (grid) => {
            grid?.querySelectorAll('[data-spirit-collection-search-item]').forEach((item) => {
              items.set(item.dataset.spiritProductId, item.cloneNode(true));
            });
          };
          const sourceSelector = isSearchPage ? '.spirit-results__inner--products' : '[data-spirit-collection-search-source]';
          const nextSelector = isSearchPage ? '[data-spirit-search-next]' : '[data-spirit-collection-source-next]';
          const initialSource = isSearchPage ? initialGrid : collectionSearchSource.content;
          collectItems(initialSource);
          const initialNext = initialSource?.querySelector(nextSelector)?.getAttribute('href');
          let nextUrl = initialNext ? new URL(initialNext, window.location.href).toString() : '';
          const firstUrl = new URL(window.location.href);
          // Read inherited categories too, even when the store's native filter
          // is absent or only indexes the child product's own metafields.
          const reloadEmotionSource = !isSearchPage && emotionCategories().length > 0;
          if (reloadEmotionSource) {
            items.clear();
            firstUrl.searchParams.delete('filter.p.m.custom.categories');
          }
          if (reloadEmotionSource || firstUrl.searchParams.has('page') || initialGrid?.querySelector('[data-spirit-results-rail]')) {
            firstUrl.searchParams.delete('page');
            if (isSearchPage) firstUrl.searchParams.set('view', 'all');
            nextUrl = firstUrl.toString();
          }
          const visited = new Set();
          while (nextUrl && !visited.has(nextUrl)) {
            visited.add(nextUrl);
            const response = await fetch(nextUrl, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error(`Search results failed with status ${response.status}`);
            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
            const source = parsed.querySelector(sourceSelector);
            const grid = isSearchPage ? source : source?.content;
            if (!grid) throw new Error('Event results were not found');
            collectItems(grid);
            const nextHref = grid.querySelector(nextSelector)?.getAttribute('href');
            nextUrl = nextHref ? new URL(nextHref, response.url).toString() : '';
          }
          if (isSearchPage && window.SpiritCatalog) {
            const categories = await window.SpiritCatalog.searchCategories(input?.value);
            const categoryItems = await window.SpiritCatalog.categorySearchItems(categories);
            categoryItems.forEach(item => items.set(item.dataset.spiritProductId, item.cloneNode(true)));
          }
          collectionSearchSource.content.replaceChildren(...items.values());
          renderEventMetaFilterOptions();
          syncEventMetaControls();
        })().catch((error) => {
          searchSourcePromise = null;
          throw error;
        });
        return searchSourcePromise;
      };

      const getEventMetaValue = (item, filterType) => {
        if (filterType === 'month') return getEventMonthValue(item);
        if (filterType === 'location') return getEventLocationScopeValue(item);
        if (filterType === 'audience') return getEventAudienceValue(item);
        if (filterType === 'format') return getEventFormatValue(item);
        if (filterType === 'duration') return getEventDurationValue(item);
        return getEventRegionValue(item);
      };

      const getSelectedEventMetaValue = (filterType) => {
        if (filterType === 'month') return selectedEventMonth;
        if (filterType === 'location') return selectedEventLocationScope;
        if (filterType === 'audience') return selectedEventAudience;
        if (filterType === 'format') return selectedEventFormat;
        if (filterType === 'duration') return selectedEventDuration;
        return selectedEventRegion;
      };

      const getEventMetaFilterLabel = (filterType) => {
        if (filterType === 'month') return 'חודש תחילת האירוע';
        if (filterType === 'location') return 'אזור גאוגרפי';
        if (filterType === 'audience') return 'קהל';
        if (filterType === 'format') return 'פורמט';
        if (filterType === 'duration') return 'משך';
        return 'אזור';
      };

      const getEventMetaValueLabel = (filterType, value, item) => {
        if (filterType === 'month') return getEventMonthLabel(value);
        if (filterType === 'location') return getEventLocationScopeLabel(value);
        if (filterType === 'audience') return getEventAudienceLabel(item) || value;
        if (filterType === 'format') return getEventFormatLabel(value);
        if (filterType === 'duration') return getEventDurationLabel(value);
        return getEventRegionLabel(item) || value;
      };

      const renderEventMetaFilterOptions = () => {
        const sourceItems = getCollectionSourceItems();

        eventMetaFilterControls.forEach((control) => {
          const filterType = control.dataset.spiritEventMetaFilter;
          const options = control.querySelector('[data-spirit-event-meta-filter-options]');
          if (!filterType || !options) return;

          const values = new Map();
          sourceItems.forEach((item) => {
            const value = getEventMetaValue(item, filterType);
            if (value && !values.has(value)) {
              values.set(value, getEventMetaValueLabel(filterType, value, item));
            }
          });

          const entries = Array.from(values, ([value, label]) => ({ value, label }));
          entries.sort((first, second) => {
            if (filterType === 'month') return first.value.localeCompare(second.value);
            if (filterType === 'duration') {
              const durationOrder = ['1', '2', '3-5', '6-8', '9+'];
              return durationOrder.indexOf(first.value) - durationOrder.indexOf(second.value);
            }
            return first.label.localeCompare(second.label, 'he');
          });

          const selectedValue = getSelectedEventMetaValue(filterType);
          const allLabels = {
            month: 'כל החודשים',
            location: 'כל המיקומים',
            audience: 'כל הקהלים',
            format: 'כל הפורמטים',
            duration: 'כל המשכים',
            region: 'כל האזורים',
          };
          const allLabel = allLabels[filterType] || 'כל האפשרויות';
          const optionItems = [{ value: '', label: allLabel }, ...entries].map(({ value, label }) => {
            const isActive = value === selectedValue;
            const listItem = document.createElement('li');
            const button = document.createElement('button');
            const text = document.createElement('span');

            button.className = `spirit-results-toolbar__choice${isActive ? ' is-active' : ''}`;
            button.type = 'button';
            button.dataset.spiritEventMetaFilterValue = value;
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            text.textContent = label;
            button.append(text);
            listItem.append(button);
            return listItem;
          });

          options.replaceChildren(...optionItems);
        });
      };

      const syncEventMetaControls = () => {
        eventMetaFilterControls.forEach((control) => {
          const filterType = control.dataset.spiritEventMetaFilter;
          const selectedValue = getSelectedEventMetaValue(filterType);
          const label = control.querySelector('[data-spirit-event-meta-filter-label]');
          if (label) {
            const defaultLabel = getEventMetaFilterLabel(filterType);
            const selectedItem = getCollectionSourceItems().find(
              (item) => getEventMetaValue(item, filterType) === selectedValue
            );
            label.textContent = filterType === 'region' && selectedEventLocationScope === 'abroad'
              ? `${defaultLabel}: חו״ל`
              : selectedValue
              ? `${defaultLabel}: ${getEventMetaValueLabel(filterType, selectedValue, selectedItem)}`
              : defaultLabel;
          }

          control.querySelectorAll('[data-spirit-event-meta-filter-value]').forEach((button) => {
            const isActive = button.dataset.spiritEventMetaFilterValue === selectedValue &&
              !(filterType === 'region' && selectedEventLocationScope === 'abroad');
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
        });

        const sortLabel = eventMetaSort?.querySelector('[data-spirit-event-meta-sort-label]');
        if (sortLabel) {
          sortLabel.textContent = `מיון: ${selectedEventSort === 'farthest' ? 'תאריך רחוק' : 'תאריך קרוב'}`;
        }

        eventMetaSortButtons.forEach((button) => {
          const isActive = button.dataset.spiritEventMetaSortOption === selectedEventSort;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        eventAbroadButtons.forEach((button) => {
          const isActive = selectedEventLocationScope === 'abroad';
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      };

      const isEventSubfilterVisible = (filterKey) => {
        if (isSearchPage) return ['event-type', 'date-range', 'region'].includes(filterKey);
        const allowedFilters = new Set(eventSubfiltersByProductType[selectedEventType] || []);
        return allowedFilters.has(filterKey);
      };

      const syncConditionalEventFilters = () => {
        eventSubfilterControls.forEach((control) => {
          const filterKey = control.dataset.spiritEventSubfilter;
          if (filterKey === 'category') {
            control.hidden = false;
            return;
          }
          const isVisible = isEventSubfilterVisible(filterKey);

          control.hidden = !isVisible;
          if (!isVisible) control.removeAttribute('open');
        });
      };

      const clearUnavailableEventMetaSelections = () => {
        if (!isEventSubfilterVisible('region')) selectedEventRegion = '';
        if (!isEventSubfilterVisible('location')) selectedEventLocationScope = '';
        if (!isEventSubfilterVisible('date-range')) selectedEventMonth = '';
        if (!isEventSubfilterVisible('audience')) selectedEventAudience = '';
        if (!isEventSubfilterVisible('format')) selectedEventFormat = '';
        if (!isEventSubfilterVisible('duration')) selectedEventDuration = '';
      };

      const clearUnavailableEventFilterParams = (url) => {
        if (isSearchPage) return;
        const allowedFilters = new Set(eventSubfiltersByProductType[selectedEventType] || []);

        eventSubfilterControls.forEach((control) => {
          const filterKey = control.dataset.spiritEventSubfilter;
          if (!filterKey || filterKey === 'event-type' || filterKey === 'category' || allowedFilters.has(filterKey)) return;

          (control.dataset.spiritEventFilterParams || '')
            .split(/\s+/)
            .filter(Boolean)
            .forEach((paramName) => url.searchParams.delete(paramName));
        });

        if (!allowedFilters.has('region')) url.searchParams.delete('event_region');
        if (!allowedFilters.has('location')) url.searchParams.delete('event_location_scope');
        if (!allowedFilters.has('date-range')) url.searchParams.delete('event_month');
        if (!allowedFilters.has('audience')) url.searchParams.delete('event_audience');
        if (!allowedFilters.has('format')) url.searchParams.delete('event_format');
        if (!allowedFilters.has('duration')) url.searchParams.delete('event_duration');
      };

      const syncEventTypeControls = () => {
        const eventTypeLabels = {
          all: 'סוג אירוע',
          workshop: 'סדנאות',
          retreat: 'ריטריטים',
          course: 'קורסים',
          festival: 'פסטיבלים',
          show: 'הופעות',
          online: 'אונליין',
        };

        eventTypeButtons.forEach((button) => {
          const isActive = button.dataset.spiritEventType === selectedEventType;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        const eventTypeLabel = eventTypeControls?.querySelector('[data-spirit-event-type-label]');
        if (eventTypeLabel) {
          eventTypeLabel.textContent = eventTypeLabels[selectedEventType] || eventTypeLabels.all;
        }

        syncConditionalEventFilters();

        if (!sortForm || !eventTypeControls) return;

        let eventTypeInput = sortForm.querySelector('input[data-spirit-event-type-input]');
        if (selectedEventType === 'all') {
          eventTypeInput?.remove();
          return;
        }

        if (!eventTypeInput) {
          eventTypeInput = document.createElement('input');
          eventTypeInput.type = 'hidden';
          eventTypeInput.name = 'event_type';
          eventTypeInput.dataset.spiritEventTypeInput = 'true';
          sortForm.append(eventTypeInput);
        }

        eventTypeInput.value = selectedEventType;
      };

      const updateEventToolbarUrl = () => {
        const url = new URL(window.location.href);
        clearUnavailableEventFilterParams(url);
        if (selectedEventType === 'all' || isSearchPage) {
          url.searchParams.delete('event_type');
        } else {
          url.searchParams.set('event_type', selectedEventType);
        }
        if (selectedEventMonth) {
          url.searchParams.set('event_month', selectedEventMonth);
        } else {
          url.searchParams.delete('event_month');
        }
        if (selectedEventRegion) {
          url.searchParams.set('event_region', selectedEventRegion);
        } else {
          url.searchParams.delete('event_region');
        }
        if (selectedEventLocationScope) {
          url.searchParams.set('event_location_scope', selectedEventLocationScope);
        } else {
          url.searchParams.delete('event_location_scope');
        }
        if (selectedEventAudience) {
          url.searchParams.set('event_audience', selectedEventAudience);
        } else {
          url.searchParams.delete('event_audience');
        }
        if (selectedEventFormat) {
          url.searchParams.set('event_format', selectedEventFormat);
        } else {
          url.searchParams.delete('event_format');
        }
        if (selectedEventDuration) {
          url.searchParams.set('event_duration', selectedEventDuration);
        } else {
          url.searchParams.delete('event_duration');
        }
        if (eventMetaSortApplied) {
          url.searchParams.set('event_sort', selectedEventSort);
        } else {
          url.searchParams.delete('event_sort');
        }
        url.searchParams.delete('page');
        window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
      };

      const applyResult = ({ gridMarkup, countMarkup, countText }, term) => {
        const currentGrid = targetId ? document.getElementById(targetId) : null;
        const nextGrid = createElementFromMarkup(gridMarkup);
        if (!currentGrid || !nextGrid) return false;

        nextGrid.id = targetId;
        nextGrid.removeAttribute('aria-busy');
        currentGrid.replaceWith(nextGrid);
        initializeResults(nextGrid);

        if (count && countMarkup) count.innerHTML = countMarkup;
        setStatus(countText);
        lastAppliedTerm = term;
        return true;
      };

      const applyCollectionResult = (term) => {
        const currentGrid = targetId ? document.getElementById(targetId) : null;
        const nextGrid = initialGrid?.cloneNode(true);
        const nextList = nextGrid?.querySelector('.spirit-results__product-grid, .spirit-results__rail--products');
        if (!currentGrid || !nextGrid || !nextList || !collectionSearchSource?.content) return false;

        const normalizedTerm = term.toLocaleLowerCase();
        const sourceItems = Array.from(
          collectionSearchSource.content.querySelectorAll('[data-spirit-collection-search-item]')
        );
        const requiredCategories = emotionCategories();
        const matchingItems = sourceItems.filter((item) => {
          const itemCategories = (item.dataset.spiritEventCategories || '').split(',');
          const matchesEmotion = requiredCategories.length === 0 || requiredCategories.some((handle) => itemCategories.includes(handle));
          const matchesSearch = isSearchPage || (item.dataset.spiritSearchText || item.textContent || '')
            .toLocaleLowerCase()
            .includes(normalizedTerm);
          const matchesMonth =
            !selectedEventMonth || getEventMonthValue(item) === selectedEventMonth;
          const matchesRegion =
            !selectedEventRegion || getEventRegionValue(item) === selectedEventRegion;
          const matchesLocationScope =
            !selectedEventLocationScope ||
            getEventLocationScopeValue(item) === selectedEventLocationScope;
          const matchesAudience =
            !selectedEventAudience || getEventAudienceValue(item) === selectedEventAudience;
          const matchesFormat =
            !selectedEventFormat || getEventFormatValue(item) === selectedEventFormat;
          const matchesDuration =
            !selectedEventDuration || getEventDurationValue(item) === selectedEventDuration;
          return (
            matchesEmotion &&
            matchesSearch &&
            matchesMonth &&
            matchesRegion &&
            matchesLocationScope &&
            matchesAudience &&
            matchesFormat &&
            matchesDuration &&
            collectionItemMatchesProductType(item, selectedEventType)
          );
        });

        if (eventMetaSortApplied) {
          matchingItems.sort((first, second) => {
            const firstTimestamp = getEventStartTimestamp(first);
            const secondTimestamp = getEventStartTimestamp(second);
            if (!Number.isFinite(firstTimestamp)) return Number.isFinite(secondTimestamp) ? 1 : 0;
            if (!Number.isFinite(secondTimestamp)) return -1;
            return selectedEventSort === 'farthest'
              ? secondTimestamp - firstTimestamp
              : firstTimestamp - secondTimestamp;
          });
        }

        nextList.replaceChildren(...matchingItems.map((item) => item.cloneNode(true)));
        nextGrid.querySelector('.spirit-results__pagination')?.remove();
        nextGrid.querySelector('[data-spirit-search-next]')?.remove();
        nextGrid.querySelectorAll('.spirit-results__empty').forEach((message) => message.remove());

        if (matchingItems.length === 0) {
          const emptyMessage = document.createElement('p');
          emptyMessage.className = 'spirit-results__empty';
          emptyMessage.setAttribute('role', 'status');
          const heading = document.createElement('strong');
          heading.textContent = form.dataset.noResultsHeading || '';
          emptyMessage.append(heading, document.createTextNode(form.dataset.noResultsText || ''));
          nextList.after(emptyMessage);
        }

        nextGrid.id = targetId;
        nextGrid.removeAttribute('aria-busy');
        currentGrid.replaceWith(nextGrid);
        initializeResults(nextGrid);

        const countValue = count?.querySelector('[data-spirit-results-count-value]');
        if (countValue) countValue.textContent = matchingItems.length.toString();
        setStatus(count?.textContent.trim() || matchingItems.length.toString());
        lastAppliedTerm = term;
        return true;
      };

      const restoreInitialResults = () => {
        const currentGrid = targetId ? document.getElementById(targetId) : null;
        const restoredGrid = initialGrid?.cloneNode(true);
        if (!currentGrid || !restoredGrid) return;

        restoredGrid.id = targetId;
        currentGrid.replaceWith(restoredGrid);
        initializeResults(restoredGrid);
        if (count) count.innerHTML = initialCountMarkup;
        setStatus(initialCountText);
        lastAppliedTerm = '';
      };

      const fetchLiveResults = async (term) => {
        if (!input || !targetId || !initialGrid) return;

        if (isSearchPage) {
          setLoading(true);
          try {
            await loadResultSource();
            applyCollectionResult(term);
          } catch (error) {
            setStatus(form.dataset.errorText || '');
          } finally {
            setLoading(false);
          }
          return;
        }

        if (
          !term &&
          selectedEventType === 'all' &&
          !selectedEventMonth &&
          !selectedEventRegion &&
          !selectedEventLocationScope &&
          !selectedEventAudience &&
          !selectedEventFormat &&
          !selectedEventDuration &&
          !eventMetaSortApplied
        ) {
          restoreInitialResults();
          return;
        }

        if (collectionSearchSource) {
          setLoading(true);
          try {
            await loadResultSource();
            applyCollectionResult(normalizeEventSearchTerm(input.value || ''));
          } catch (error) {
            setStatus(form.dataset.errorText || '');
          } finally {
            setLoading(false);
          }
          return;
        }

        if (term === lastAppliedTerm) return;

        abortController?.abort();
        abortController = new AbortController();
        setLoading(true);
        setStatus(form.dataset.loadingText || '');

        try {
          let result = eventSearchCache.get(term);

          if (!result) {
            const params = new URLSearchParams(new FormData(form));
            params.set('q', getEventSearchQuery(term));
            const response = await fetch(`${form.action}?${params.toString()}`, {
              credentials: 'same-origin',
              headers: { Accept: 'text/html' },
              signal: abortController.signal,
            });

            if (!response.ok) throw new Error(`Live search failed with status ${response.status}`);

            const html = await response.text();
            const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
            const nextSection = parsedDocument.querySelector('[data-spirit-results]');
            const nextGrid = nextSection?.querySelector('.spirit-results__inner--products');
            const nextCount = nextSection?.querySelector('.spirit-results-hero__count');

            if (!nextGrid) throw new Error('Live search results were not found');

            result = {
              gridMarkup: nextGrid.outerHTML,
              countMarkup: nextCount?.innerHTML || '',
              countText:
                nextCount?.textContent.trim() || nextGrid.querySelector('[role="status"]')?.textContent.trim() || '',
            };
            eventSearchCache.set(term, result);
          }

          applyResult(result, term);
        } catch (error) {
          if (error.name !== 'AbortError') setStatus(form.dataset.errorText || '');
        } finally {
          setLoading(false);
        }
      };

      const refreshCollectionResults = () => {
        window.clearTimeout(debounceTimer);
        abortController?.abort();
        fetchLiveResults(normalizeEventSearchTerm(input?.value || ''));
      };

      const scheduleLiveSearch = () => {
        window.clearTimeout(debounceTimer);
        abortController?.abort();
        const term = normalizeEventSearchTerm(input?.value || '');
        debounceTimer = window.setTimeout(() => fetchLiveResults(term), delay);
      };

      input?.addEventListener('input', (event) => {
        if (!isSearchPage && !event.isComposing) scheduleLiveSearch();
      });
      if (!isSearchPage) input?.addEventListener('compositionend', scheduleLiveSearch);

      eventAbroadButtons.forEach((button) => {
        button.addEventListener('click', () => {
          selectedEventLocationScope = selectedEventLocationScope === 'abroad' ? '' : 'abroad';
          selectedEventRegion = '';
          button.closest('details')?.removeAttribute('open');
          syncEventMetaControls();
          updateEventToolbarUrl();
          refreshCollectionResults();
        });
      });

      eventAbroadLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();

          const url = new URL(link.href, window.location.origin);
          const locationParam = link.dataset.spiritEventLocationParam;
          const locationValue = link.dataset.spiritEventLocationValue;
          const regionParam = link.dataset.spiritEventRegionParam;

          url.searchParams.delete('page');
          url.searchParams.delete('event_region');
          if (regionParam) url.searchParams.delete(regionParam);
          if (locationParam) {
            url.searchParams.delete(locationParam);
            if (link.dataset.spiritEventAbroadActive !== 'true' && locationValue) {
              url.searchParams.set(locationParam, locationValue);
            }
          }

          window.location.assign(`${url.pathname}${url.search}${url.hash}`);
        });
      });

      const buildSingleSelectFilterUrl = (link) => {
        const url = new URL(window.location.href);
        const paramName = link.dataset.spiritSingleSelectFilterParam;
        const paramValue = link.dataset.spiritSingleSelectFilterValue;
        const isActive = link.classList.contains('is-active') || link.getAttribute('aria-current') === 'true';

        if (!paramName) return url;

        url.searchParams.delete('page');
        url.searchParams.delete(paramName);
        if (!isActive && paramValue) url.searchParams.set(paramName, paramValue);
        if (link.closest('[data-spirit-event-subfilter="region"]')) {
          url.searchParams.delete('event_location_scope');
          eventAbroadLinks.forEach((abroadLink) => {
            const locationParam = abroadLink.dataset.spiritEventLocationParam;
            if (locationParam) url.searchParams.delete(locationParam);
          });
        }
        if (link.hasAttribute('data-spirit-event-type-value')) url.searchParams.delete('event_type');

        return url;
      };

      const normalizeSingleSelectFilterParams = () => {
        const url = new URL(window.location.href);
        const filterParams = new Set(
          singleSelectFilterLinks
            .map((link) => link.dataset.spiritSingleSelectFilterParam)
            .filter(Boolean)
        );
        let shouldReplace = false;

        filterParams.forEach((paramName) => {
          let selectedValues = url.searchParams
            .getAll(paramName)
            .flatMap((value) => value.split(','))
            .filter(Boolean);

          if (paramName === 'filter.p.product_type' && selectedValues.length === 0) {
            const legacyEventType = normalizeProductType(url.searchParams.get('event_type'));
            const matchingLink = nativeEventTypeLinks.find(
              (link) => normalizeProductType(link.dataset.spiritEventTypeValue) === legacyEventType
            );
            const matchingValue = matchingLink?.dataset.spiritSingleSelectFilterValue || legacyEventType;

            if (legacyEventType && legacyEventType !== 'all' && matchingValue) {
              selectedValues = [matchingValue];
              shouldReplace = true;
            }
          }

          if (selectedValues.length > 1) shouldReplace = true;
          if (selectedValues.length > 0) {
            url.searchParams.delete(paramName);
            url.searchParams.set(paramName, selectedValues[selectedValues.length - 1]);
          }

          if (paramName === 'filter.p.product_type' && url.searchParams.has('event_type')) {
            url.searchParams.delete('event_type');
            shouldReplace = true;
          }
        });

        if (shouldReplace) {
          window.location.replace(`${url.pathname}${url.search}${url.hash}`);
          return true;
        }

        singleSelectFilterLinks.forEach((link) => {
          link.href = buildSingleSelectFilterUrl(link).toString();
        });
        return false;
      };

      if (normalizeSingleSelectFilterParams()) return;

      nativeEventTypeLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }

          const isActive = link.classList.contains('is-active') || link.getAttribute('aria-current') === 'true';
          const nextEventType = isActive
            ? 'all'
            : normalizeProductType(link.dataset.spiritEventTypeValue) || 'all';
          const url = new URL(link.href, window.location.origin);

          selectedEventType = nextEventType;
          clearUnavailableEventMetaSelections();
          clearUnavailableEventFilterParams(url);
          url.searchParams.delete('event_type');

          event.preventDefault();
          window.location.assign(`${url.pathname}${url.search}${url.hash}`);
        });
      });

      form.addEventListener('submit', (event) => {
        if (!input) return;

        window.clearTimeout(debounceTimer);
        abortController?.abort();

        if (isSearchPage) {
          const params = new URLSearchParams(window.location.search);
          ['event_month', 'event_region', 'event_sort'].forEach((name) => {
            form.querySelector(`input[name="${name}"]`)?.remove();
            if (!params.has(name)) return;
            const field = document.createElement('input');
            field.type = 'hidden';
            field.name = name;
            field.value = params.get(name);
            form.append(field);
          });
          return;
        }

        if (collectionSearchSource) {
          event.preventDefault();
          fetchLiveResults(normalizeEventSearchTerm(input.value));
          return;
        }

        input.value = getEventSearchQuery(normalizeEventSearchTerm(input.value));
      });

      if (
        eventMetaFilters &&
        eventMetaFilters.dataset.spiritEventMetaFiltersReady !== 'true'
      ) {
        renderEventMetaFilterOptions();
        syncEventMetaControls();

        eventMetaFilterControls.forEach((control) => {
          const filterType = control.dataset.spiritEventMetaFilter;
          const options = control.querySelector('[data-spirit-event-meta-filter-options]');
          if (!filterType || !options) return;

          options.addEventListener('click', (event) => {
            const button = event.target.closest('[data-spirit-event-meta-filter-value]');
            if (!button) return;

            const nextValue = normalizeEventMetaValue(button.dataset.spiritEventMetaFilterValue);
            if (
              (filterType === 'month' && nextValue === selectedEventMonth) ||
              (filterType === 'region' && nextValue === selectedEventRegion && !selectedEventLocationScope) ||
              (filterType === 'location' && nextValue === selectedEventLocationScope) ||
              (filterType === 'audience' && nextValue === selectedEventAudience) ||
              (filterType === 'format' && nextValue === selectedEventFormat) ||
              (filterType === 'duration' && nextValue === selectedEventDuration)
            ) {
              control.removeAttribute('open');
              return;
            }

            if (filterType === 'month') {
              selectedEventMonth = nextValue;
            } else if (filterType === 'location') {
              selectedEventLocationScope = normalizeEventLocationScope(nextValue);
            } else if (filterType === 'audience') {
              selectedEventAudience = nextValue;
            } else if (filterType === 'format') {
              selectedEventFormat = nextValue;
            } else if (filterType === 'duration') {
              selectedEventDuration = nextValue;
            } else {
              selectedEventRegion = nextValue;
              selectedEventLocationScope = '';
            }

            control.removeAttribute('open');
            renderEventMetaFilterOptions();
            syncEventMetaControls();
            updateEventToolbarUrl();
            refreshCollectionResults();
          });
        });

        eventMetaSortButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const nextSort = button.dataset.spiritEventMetaSortOption;
            if (!['nearest', 'farthest'].includes(nextSort)) return;
            if (nextSort === selectedEventSort && eventMetaSortApplied) {
              eventMetaSort?.removeAttribute('open');
              return;
            }

            selectedEventSort = nextSort;
            eventMetaSortApplied = true;
            eventMetaSort?.removeAttribute('open');
            syncEventMetaControls();
            updateEventToolbarUrl();
            refreshCollectionResults();
          });
        });

        window.addEventListener('popstate', () => {
          const params = new URLSearchParams(window.location.search);
          selectedEventMonth = normalizeEventMetaValue(params.get('event_month'));
          selectedEventRegion = normalizeEventMetaValue(params.get('event_region'));
          selectedEventLocationScope = normalizeEventLocationScope(params.get('event_location_scope'));
          selectedEventAudience = normalizeEventMetaValue(params.get('event_audience'));
          selectedEventFormat = normalizeEventMetaValue(params.get('event_format'));
          selectedEventDuration = normalizeEventMetaValue(params.get('event_duration'));
          selectedEventSort = params.get('event_sort') || 'nearest';
          eventMetaSortApplied = defaultEventSortApplied || params.has('event_sort');

          if (!['nearest', 'farthest'].includes(selectedEventSort)) {
            selectedEventSort = 'nearest';
            eventMetaSortApplied = defaultEventSortApplied;
          }

          renderEventMetaFilterOptions();
          syncEventMetaControls();
          refreshCollectionResults();
        });

        if (
          selectedEventMonth ||
          selectedEventRegion ||
          selectedEventLocationScope ||
          selectedEventAudience ||
          selectedEventFormat ||
          selectedEventDuration ||
          eventMetaSortApplied
        ) {
          refreshCollectionResults();
        }
        eventMetaFilters.dataset.spiritEventMetaFiltersReady = 'true';
      }

      syncConditionalEventFilters();

      if (
        eventTypeControls &&
        eventTypeButtons.length > 0 &&
        eventTypeControls.dataset.spiritEventTypesReady !== 'true'
      ) {
        eventTypeButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const nextEventType = normalizeProductType(button.dataset.spiritEventType) || 'all';
            if (nextEventType === selectedEventType) return;

            if (isSearchPage) {
              const url = new URL(window.location.href);
              url.searchParams.delete('page');
              url.searchParams.delete('event_type');
              url.searchParams.delete('filter.p.product_type');
              if (nextEventType !== 'all') url.searchParams.set('filter.p.product_type', nextEventType);
              window.location.assign(url.toString());
              return;
            }

            selectedEventType = nextEventType;
            clearUnavailableEventMetaSelections();
            renderEventMetaFilterOptions();
            syncEventMetaControls();
            syncEventTypeControls();
            updateEventToolbarUrl();
            refreshCollectionResults();
            eventTypeControls.removeAttribute('open');
          });
        });

        window.addEventListener('popstate', () => {
          const nextEventType =
            normalizeProductType(new URLSearchParams(window.location.search).get('event_type')) || 'all';
          if (nextEventType === selectedEventType) return;

          selectedEventType = nextEventType;
          clearUnavailableEventMetaSelections();
          renderEventMetaFilterOptions();
          syncEventMetaControls();
          syncEventTypeControls();
          refreshCollectionResults();
        });

        syncEventTypeControls();
        if (selectedEventType !== 'all') refreshCollectionResults();
        eventTypeControls.dataset.spiritEventTypesReady = 'true';
      }

      if (emotionCategories().length > 0) refreshCollectionResults();
      if (isSearchPage && window.SpiritCatalog) {
        window.SpiritCatalog.searchCategories(input?.value).then(categories => {
          if (categories.length && form.isConnected) refreshCollectionResults();
        }).catch(() => {});
      }
      form.dataset.spiritEventSearchReady = 'true';
    });

    root.querySelectorAll('[data-spirit-category-controls]').forEach((controls) => {
      if (controls.dataset.spiritCategoryReady === 'true') return;

      const railId = controls.dataset.spiritCategoryControls;
      const rail = document.getElementById(railId);
      if (!rail) return;

      const scrollButtons = Array.from(controls.querySelectorAll('[data-spirit-category-scroll]'));
      let categoryScrollFrame;

      const updateCategoryScrollButtons = () => {
        window.cancelAnimationFrame(categoryScrollFrame);
        categoryScrollFrame = window.requestAnimationFrame(() => {
          const items = Array.from(rail.querySelectorAll(':scope > li'));
          const railRect = rail.getBoundingClientRect();
          const itemRects = items.map((item) => item.getBoundingClientRect());
          const noOverflow = rail.scrollWidth <= rail.clientWidth + 2 || itemRects.length === 0;
          const atLeft = noOverflow || Math.min(...itemRects.map((rect) => rect.left)) >= railRect.left - 2;
          const atRight = noOverflow || Math.max(...itemRects.map((rect) => rect.right)) <= railRect.right + 2;

          scrollButtons.forEach((button) => {
            const direction = Number.parseInt(button.dataset.spiritCategoryScroll, 10) || 0;
            const disabled = direction < 0 ? atLeft : atRight;
            button.hidden = noOverflow;
            button.disabled = disabled;
            button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
          });
        });
      };

      const scrollRail = (logicalDirection) => {
        const item = rail.querySelector('li');
        if (!item) return;

        const styles = window.getComputedStyle(rail);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const step = item.getBoundingClientRect().width + gap;
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

        rail.scrollBy({ left: logicalDirection * step, behavior });
      };

      const revealActiveCategory = () => {
        const activeLink = rail.querySelector('a[aria-current="true"], a.is-active');
        const activeItem = activeLink?.closest('li') || activeLink;
        if (!activeItem) return;

        window.requestAnimationFrame(() => {
          if (!rail.isConnected || !activeItem.isConnected) return;

          const railRect = rail.getBoundingClientRect();
          const activeRect = activeItem.getBoundingClientRect();
          const isFullyVisible = activeRect.left >= railRect.left - 2 && activeRect.right <= railRect.right + 2;
          if (isFullyVisible) return;

          const railCenter = railRect.left + railRect.width / 2;
          const activeCenter = activeRect.left + activeRect.width / 2;
          rail.scrollBy({ left: activeCenter - railCenter, behavior: 'auto' });
          updateCategoryScrollButtons();
        });
      };

      scrollButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          scrollRail(Number.parseInt(button.dataset.spiritCategoryScroll, 10) || 0);
        });
      });

      rail.addEventListener('scroll', updateCategoryScrollButtons, { passive: true });
      window.addEventListener('resize', updateCategoryScrollButtons);
      if ('ResizeObserver' in window) {
        new ResizeObserver(updateCategoryScrollButtons).observe(rail);
      }
      revealActiveCategory();
      updateCategoryScrollButtons();

      controls.dataset.spiritCategoryReady = 'true';
    });

    root.querySelectorAll('[data-spirit-category-disclosure]').forEach((disclosure) => {
      if (disclosure.dataset.spiritCategoryDisclosureReady === 'true') return;

      const toggle = disclosure.querySelector('[data-spirit-category-toggle]');
      if (!toggle) return;

      const shell = disclosure.closest('[data-spirit-mobile-filter-shell]');
      const filterToggle = shell?.querySelector('[data-spirit-mobile-filter-toggle]');
      const categoryToggle = shell?.querySelector('[data-spirit-mobile-category-toggle]');
      const categoryProxy = shell?.querySelector('[data-spirit-mobile-category-proxy]');

      const setMode = (mode) => {
        const categoriesOpen = mode === 'categories';
        const filtersOpen = mode === 'filters';

        shell?.classList.toggle('is-mobile-categories-open', categoriesOpen);
        shell?.classList.toggle('is-mobile-filters-open', filtersOpen);
        disclosure.classList.toggle('is-open', categoriesOpen);
        toggle.setAttribute('aria-expanded', categoriesOpen ? 'true' : 'false');
        categoryToggle?.setAttribute('aria-expanded', categoriesOpen ? 'true' : 'false');
        categoryProxy?.setAttribute('aria-expanded', categoriesOpen ? 'true' : 'false');
        filterToggle?.setAttribute('aria-expanded', filtersOpen ? 'true' : 'false');
        shell
          ?.closest('[data-spirit-results]')
          ?.querySelectorAll('[data-spirit-mobile-collection-heading]')
          .forEach((heading) => {
            heading.hidden = Boolean(mode);
          });

        if (filtersOpen) {
          requestAnimationFrame(() => {
            shell
              ?.querySelector('.spirit-results-toolbar__collection-filters summary')
              ?.focus();
          });
        }

        if (!filtersOpen) {
          shell?.querySelectorAll('.spirit-results-toolbar__collection-filters details[open], .spirit-results-toolbar__meta-sort[open]')
            .forEach((details) => details.removeAttribute('open'));
        }
      };

      toggle.addEventListener('click', () => {
        setMode(disclosure.classList.contains('is-open') ? null : 'categories');
      });

      disclosure.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest?.('a')) return;
          setMode(null);
        },
        true
      );

      categoryToggle?.addEventListener('click', () => {
        setMode(shell?.classList.contains('is-mobile-categories-open') ? null : 'categories');
      });

      categoryProxy?.addEventListener('click', () => setMode('categories'));

      filterToggle?.addEventListener('click', () => {
        setMode(shell?.classList.contains('is-mobile-filters-open') ? null : 'filters');
      });

      shell?.querySelector('[data-spirit-mobile-filter-close]')?.addEventListener('click', () => {
        setMode(null);
        filterToggle?.focus({ preventScroll: true });
      });

      shell?.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!shell.classList.contains('is-mobile-categories-open') && !shell.classList.contains('is-mobile-filters-open')) return;

        const focusTarget = shell.classList.contains('is-mobile-filters-open') ? filterToggle : categoryToggle;
        setMode(null);
        focusTarget?.focus();
      });

      disclosure.dataset.spiritCategoryDisclosureReady = 'true';
    });

    root.querySelectorAll('[data-spirit-rail-controls]').forEach((controls) => {
      if (controls.dataset.spiritRailReady === 'true') return;

      const railId = controls.dataset.spiritRailControls;
      const rail = document.getElementById(railId);
      if (!rail) return;

      const scrollRail = (logicalDirection) => {
        const item = rail.querySelector('.spirit-results__item');
        if (!item) return;

        const styles = window.getComputedStyle(rail);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const step = item.getBoundingClientRect().width + gap;
        const isRtl = styles.direction === 'rtl';
        const physicalDirection = isRtl ? logicalDirection * -1 : logicalDirection;

        rail.scrollBy({ left: physicalDirection * step, behavior: 'smooth' });
      };

      controls.querySelector('[data-spirit-rail-previous]')?.addEventListener('click', () => scrollRail(-1));
      controls.querySelector('[data-spirit-rail-next]')?.addEventListener('click', () => scrollRail(1));
      controls.dataset.spiritRailReady = 'true';
    });

    root.querySelectorAll('[data-spirit-results-sort]').forEach((select) => {
      if (select.dataset.spiritSortReady === 'true') return;
      select.addEventListener('change', () => select.form?.requestSubmit());
      select.dataset.spiritSortReady = 'true';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeResults());
  } else {
    initializeResults();
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!event.target.closest?.('[data-spirit-category-disclosure] a')) return;
      closeMobileFilterShells();
    },
    true
  );
  document.addEventListener('shopify:section:load', (event) => initializeResults(event.target));
  window.addEventListener('pageshow', () => closeMobileFilterShells());
  window.addEventListener('popstate', () => closeMobileFilterShells());
})();
